import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { replaceAllAction } from "store/utils";

type ChatMentionsState = {
  requestedMessageIdBySection: Record<string, string | null>;
  unseenMessageIdsBySection: Record<string, string[]>;
};

const getInitialState = (): ChatMentionsState => ({
  requestedMessageIdBySection: {},
  unseenMessageIdsBySection: {},
});

export const ChatMentionsStore = createSlice({
  name: "chatMentions",
  initialState: getInitialState,
  reducers: {
    addUnseenMention: (state, action: PayloadAction<{ messageId: string; sectionId: string }>) => {
      const { messageId, sectionId } = action.payload;
      const unseenMessageIds = state.unseenMessageIdsBySection[sectionId] ?? [];
      if (!unseenMessageIds.includes(messageId)) {
        state.unseenMessageIdsBySection[sectionId] = [...unseenMessageIds, messageId];
      }
    },
    markMentionsSeen: (
      state,
      action: PayloadAction<{ messageIds: string[]; sectionId: string }>,
    ) => {
      const { messageIds, sectionId } = action.payload;
      const seenMessageIds = new Set(messageIds);
      const unseenMessageIds = state.unseenMessageIdsBySection[sectionId];
      if (!unseenMessageIds?.some((messageId) => seenMessageIds.has(messageId))) return;

      state.unseenMessageIdsBySection[sectionId] = unseenMessageIds.filter(
        (messageId) => !seenMessageIds.has(messageId),
      );
    },
    removeDeletedMention: (
      state,
      action: PayloadAction<{ messageId: string; sectionId: string }>,
    ) => {
      const { messageId, sectionId } = action.payload;
      const unseenMessageIds = state.unseenMessageIdsBySection[sectionId];
      if (unseenMessageIds?.includes(messageId)) {
        state.unseenMessageIdsBySection[sectionId] = unseenMessageIds.filter(
          (unseenMessageId) => unseenMessageId !== messageId,
        );
      }
      if (state.requestedMessageIdBySection[sectionId] === messageId) {
        state.requestedMessageIdBySection[sectionId] = null;
      }
    },
    requestOldestUnseenMention: (state, action: PayloadAction<{ sectionId: string }>) => {
      const { sectionId } = action.payload;
      state.requestedMessageIdBySection[sectionId] =
        state.unseenMessageIdsBySection[sectionId]?.[0] ?? null;
    },
    clearMentionScrollRequest: (state, action: PayloadAction<{ sectionId: string }>) => {
      state.requestedMessageIdBySection[action.payload.sectionId] = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(replaceAllAction, (_state, action) => {
      return (action.payload?.chatMentions as ChatMentionsState | undefined) ?? getInitialState();
    });
  },
});

export const chatMentionsActions = ChatMentionsStore.actions;
